import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Share2, Loader2, HelpCircle, ArrowRight, Radio } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { EPISODES } from "@/components/Derive";

const L = {
  fr: { eyebrow: "Nouvel épisode · La Dérive", num: "Épisode", question: "La question", share: "Partager le teaser", preparing: "Préparation du teaser…", err: "Oups, réessaie dans un instant.", saved: "Teaser téléchargé — prêt à partager", all: "Tous les épisodes" },
  en: { eyebrow: "New episode · The Drift", num: "Episode", question: "The question", share: "Share the teaser", preparing: "Preparing the teaser…", err: "Oops, try again in a moment.", saved: "Teaser downloaded — ready to share", all: "All episodes" },
  es: { eyebrow: "Nuevo episodio · La Deriva", num: "Episodio", question: "La pregunta", share: "Compartir el teaser", preparing: "Preparando el teaser…", err: "Vaya, inténtalo de nuevo en un momento.", saved: "Teaser descargado — listo para compartir", all: "Todos los episodios" },
  nl: { eyebrow: "Nieuwe aflevering · De Drift", num: "Aflevering", question: "De vraag", share: "Deel de teaser", preparing: "Teaser voorbereiden…", err: "Oeps, probeer het zo opnieuw.", saved: "Teaser gedownload — klaar om te delen", all: "Alle afleveringen" },
};

const ep = EPISODES[0];

export const FeaturedEpisode = () => {
  const { i18n } = useTranslation();
  const lang = ["fr", "en", "es", "nl"].includes((i18n.language || "fr").slice(0, 2)) ? (i18n.language || "fr").slice(0, 2) : "fr";
  const T = L[lang];
  const [busy, setBusy] = useState(false);
  const title = ep.title[lang] || ep.title.fr;
  const question = (ep.q && (ep.q[lang] || ep.q.fr)) || "";

  const share = async () => {
    if (busy) return;
    setBusy(true);
    const tid = toast.loading(T.preparing);
    try {
      const res = await fetch(`/derive/teasers/${ep.slug}-${lang}.mp4`, { cache: "no-store" });
      if (!res.ok) throw new Error("teaser");
      const blob = await res.blob();
      const file = new File([blob], `la-derive-${ep.slug}-${lang}.mp4`, { type: "video/mp4" });
      api.post(`/derive/share`, null, { params: { slug: ep.slug } }).catch(() => {});
      toast.dismiss(tid);
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try { await navigator.share({ files: [file], title: `${title} — La Dérive · COCOON`, text: `${question} — cocoonmind.com/derive` }); } catch { /* cancelled */ }
        return;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = file.name;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(T.saved);
    } catch {
      toast.error(T.err, { id: tid });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="relative max-w-5xl mx-auto px-6 pt-4 pb-10" data-testid="featured-episode">
      <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.8 }}
        className="glass rounded-3xl border border-primary/20 p-5 sm:p-8 grid sm:grid-cols-[220px_1fr] gap-6 sm:gap-8 items-center shadow-[0_20px_80px_-30px_hsl(28_96%_56%_/_0.35)]">
        <img src={`/derive/covers/${ep.slug}.jpg`} alt={title} data-testid="featured-episode-cover"
          className="w-full max-w-[240px] mx-auto aspect-square rounded-2xl object-cover border border-primary/25 shadow-2xl" />
        <div className="min-w-0">
          <p className="inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.24em] text-primary/80 mb-3">
            <Radio size={13} className="animate-pulse" /> {T.eyebrow} · {T.num} {EPISODES.length}
          </p>
          <h3 className="font-display text-3xl sm:text-4xl leading-tight text-foreground mb-3" data-testid="featured-episode-title">{title}</h3>
          {question && (
            <p className="flex items-start gap-2 font-display italic text-primary/90 text-lg mb-5" data-testid="featured-episode-question">
              <HelpCircle size={16} className="mt-1.5 flex-shrink-0" /> {question}
            </p>
          )}
          <audio controls preload="none" src={`/derive/${ep.slug}.mp3`} className="w-full h-10 mb-5" data-testid="featured-episode-audio" />
          <div className="flex flex-wrap items-center gap-3">
            <button onClick={share} disabled={busy} data-testid="featured-episode-share-btn"
              className="btn-frost-orange inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold disabled:opacity-60">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Share2 size={15} />} {T.share}
            </button>
            <Link to="/derive" data-testid="featured-episode-all-link"
              className="group inline-flex items-center gap-1.5 text-sm text-foreground/60 hover:text-primary transition-colors">
              {T.all} <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </motion.div>
    </section>
  );
};
